import styled from 'styled-components';
import { Box, Button, Select, TextField } from '@mui/material';
import ArrowCircleRight from '@mui/icons-material/ArrowCircleRight';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';

export const SBox = styled(Box)`
  background-color: #FFFFFF;
  width: 100%;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  border-radius: 8px;
`;

/**
 * RTL variant of Form: labels and fields start at the right edge.
 *
 * `align-items: flex-end` alone is not enough — it aligns children within an
 * LTR column, which lands them on the left.
 */
export const FormRtl = styled.form`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  direction: rtl;
  text-align: right;

  & > * {
    margin-bottom: 16px;
  }
`;

export const Form = styled.form`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 768px) {
    max-width: 350px;
  }

  & > * {
    margin-bottom: 16px;
  }
`;

export const CustomHelperText = styled.span<{ error: boolean }>`
  color: ${({ error }) => (error ? 'red' : 'gray')};
  font-size: 10px;
  margin-top: 4px;
  /* direction (not just text-align) so Persian punctuation sits on the left end */
  direction: rtl;
  text-align: right;
  display: block;
`;

export const TextFieldWrapper = styled.div`
  width: 100%;
  min-height: 48px;
  position: relative;
  margin-bottom: 8px;
`;

export const TextFieldBackground = styled.div`
  width: 100%;
  height: 48px;
  position: absolute;
  border-radius: 8px;
  border: 1px solid #D9D9DD;
  text-align: right;
`;

export const STextField = styled(TextField)`
  width: 100%;
  border-radius: 8px;
  margin: 0;

  /* The 48px belongs to the input box, not the whole field: the TextField
     also contains the helper text, so fixing its height clips the error. */
  .MuiOutlinedInput-root {
    height: 48px;
    input {
      color: #484849;
      font-family: 'IRANYekanXFaNum';
      font-size: 14px;
      font-weight: 600;
    }

    fieldset {
      border: none;
    }

    input::placeholder {
      color: #BBC0CB;
      font-family: 'IRANYekanXFaNum';
      font-size: 14px;
      font-weight: 600;
    }
  }

  /* MUI wraps helperText in a <p> that is LTR and left-indented by default */
  .MuiFormHelperText-root {
    direction: rtl;
    text-align: right;
    margin: 4px 0 0 0;
  }
`;

export const LabelInit = styled.label`
  /* flex-direction/justify-content below only apply once this is a flex
     container; without it the two labels stacked instead of sitting inline. */
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex-direction: row;
  color: #A6AAB5;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 600;
  justify-content: start;
  line-height: 18.2px;
  padding-bottom: 0px;
  height: 20px;
`;

export const BoxPublish = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #a6aab5;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 400;
  line-height: 18.2px;
  padding-bottom: 8px;
  width: 100%;
`;

export const LabelPublich = styled.label`
  color: #35373c;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 400;
  text-align: right;
  line-height: 18.2px;
`;

export const Description = styled.label`
  color: #35373C;
  font-size: 12px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 400;
  text-align: right;
  line-height: 18px;
  padding-top: 0px;
`;

export const Label = styled.label`
  color: #35373C;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  text-align: right;
  line-height: 18.2px;
  padding-bottom: 2px;
  padding-top: 8px;
  margin-bottom: 16px;
`;

export const titleField = styled.label`
  color: #35373C;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  text-align: right;
  line-height: 18.2px;
  padding-bottom: 0px;
`;

export const LableInputMobile = styled(Label)`
  margin: 2px 0 24px 1px;
`;

export const LinkContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 1px;
`;

export const ClickableText = styled.span`
  cursor: pointer;
  color: #60646E;
  font-size: 12px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

export const ClickableTextDetails = styled(Label)`
  margin: 2px 0 12px 12px;
  margin-bottom: 0px;
  font-family: 'IRANYekanXFaNum';
  font-size: 12px;
  color: #35373C;
`;

export const ClickableTextBlue = styled(ClickableText)`
  color: #0066cc;
`;

export const ClickableTextRegister = styled(ClickableText)`
  color: #0066cc;
  font-size: 14px;
  font-weight: 700;
`;

export const Divider = styled('div')`
  width: 100%;
  border: 0.5px solid #ccc;
  margin: 16px 0;
  border: 1px #D9D9DD dotted;
  margin: 2px 0 20px 20px;
`;

export const SubTitle = styled.h6`
  margin: 2px 0 12px 12px;
  color: #25282E;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  line-height: normal;
  text-align: right;
`;

export const SubTitle2 = styled.h3`
  margin: 2px 0 12px 12px;
  direction: ltr;
  color: #25282E;
  font-size: 18px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  line-height: 15.6px;
  text-align: left;

  .blue-text {
    color: #1E88E5;
  }
`;

export const Title = styled.div`
  color: #25282E;
  font-size: 20px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 900;
  margin: 2px 2px 2px 0px;
  line-height: 26px;
  word-wrap: break-word;
  text-align: right;
  @media (max-width: 768px) {
    max-width: 310px;
    font-size: 15px;
  }
`;

export const IdenttyBox = styled(Box)`
  flex-direction: row;
  color: White & Black/11;
  font-size: 16px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 600;
  margin: 2px 0 12px 20px;
  text-align: right;
  line-height: 18.2px;
  align-items: right;
`;

export const DetailsBox = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 360px;
  height: 84px;
  border-bottom: 1px solid #D9D9DD;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  @media (max-width: 768px) {
    max-width: 310px;
  }
`;

export const SelectField = styled(Select)`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  font-family: 'IRANYekanXFaNum';
  font-size: 14px;
  direction: rtl;
  text-align: right;

  .MuiSelect-select {
    text-align: right;
  }

  .MuiSelect-icon {
    right: auto;
    left: 8px;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 16px;
`;

export const SubmitButtonBox = styled.div`
  padding-top: 8px;
  display: flex;
  justify-content: left;
  width: 100%;
`;

export const ReturnStepOne = styled(Button)`
  && {
    width: 76px;
    height: 36px;
    margin-bottom: 16px;
    background-color: #ffffff;
    color: #35373c;
    font-size: 11px;
    font-family: 'IRANYekanXFaNum';
    font-weight: 500;
    line-height: 16px;
    padding: 1px;
    padding-right: 0px;
    justify-content: space-between;
    text-transform: none;
    text-align: right;
    box-shadow: none;
    border: 1px solid #ededed;

    &:hover {
      background-color: #ededed;
    }
  }
`;

export const ReturnStepTow = styled(ReturnStepOne)`
  && {
    width: 192px;
  }
`;

export const Icon = styled(ArrowCircleRight)`
  color: gray;
  width: 22px;
  height: 22px;
  margin-right: 4px;
`;

export const ButtonHelp = styled('div')`
  && {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    margin-bottom: 16px;
    background-color: #EBF5FF;
    color: #1E88E5;
    font-size: 11px;
    font-family: 'IRANYekanXFaNum';
    font-weight: 500;
    line-height: 16px;
    text-transform: none;
    text-align: right;
    box-shadow: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 12px;
    &:hover {
      background-color: #ededed;
    }
  }
`;

export const IconHelp = styled(OpenInNewOutlined)`
  color: #1E88E5;
  width: 100%;
  height: 22px;
  align-items: center;
  justify-content: center;
  margin-right: 0px;
`;

export const FieldContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  margin-top: 85px;
`;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 16px;
  row-gap: 16px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

/**
 * Form card, used both on its own (the wizard) and inside a Grid column on the
 * edit page.
 *
 * `max-width`, not `width`: 520px matches StopperDeveloper above it in the
 * wizard, while still letting a narrower column shrink it. A hard width
 * overflowed that column and pushed the contents past the card's edge.
 *
 * Sections that span a full-width row pass `fluid` to drop the cap, otherwise
 * they render at 520px and sit visibly narrower than the cards beside them.
 */
export const Container = styled.div<{ fluid?: boolean }>`
  width: 100%;
  height: auto;
  ${({ fluid }) => (fluid ? '' : 'max-width: 520px;')}
  background-color: white;
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: flex-start;
  border-radius: 12px;
  border: 1px solid #ededed;
  gap: 8px;
  box-sizing: border-box;
`;

export const ReturnStepThree = styled(ReturnStepOne)`
  && {
    width: 162px;
  }
`;

export const DiscriptionBox = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  @media screen and (max-width: 768px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
  }
`;

export const DescriptionFieldLeft = styled(TextField)`
  && {
    /* Fluid: the two description fields sit side by side in DiscriptionBox,
       so a fixed width made them mismatch once the container resized. */
    width: 100%;
    height: 200px;
    box-sizing: border-box;
    direction: ltr;
    overflow: hidden;
    overflow-wrap: break-word;
    resize: none;
   border-radius: 8px;
   border: rgba(63, 81, 181, 0.2) 1px solid;

    .MuiInputBase-root {
      width: 100%;
      height: 100%;
      align-items: flex-start;
      overflow-y: auto;
      box-sizing: border-box;
      font-family: 'IRANYekanXFaNum';
      font-size: 14px;
      padding: 12px;
      border-radius: 8px;
    }

    .MuiOutlinedInput-root {
      width: 100%;
      height: 100%;
      border-radius: 8px;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
      border: none; /* Remove border */

      &:hover fieldset {
        border: none; /* No border on hover */
      }

      &.Mui-focused fieldset {
        border: none; /* No border when focused */
        box-shadow: 0px 6px 20px rgba(63, 81, 181, 0.2);
      }

      fieldset {
        border: none; /* Ensure fieldset has no border */
        width: 100%;
        height: 100%;
        border-radius: 8px;
      }
    }

    @media screen and (max-width: 768px) {
      height: 150px;
    }
  }
`;

export const DescriptionFieldRight = styled(DescriptionFieldLeft)`
  && {
    direction: rtl;
  }
`;

export const DescriptionFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* Equal halves of DiscriptionBox. min-width:0 lets a flex item shrink
     below its content width, which is what kept the columns uneven. */
  flex: 1 1 0;
  min-width: 0;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

export const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

export const SubmitBox = styled.div`
  bottom: 0;
  left: 0;
  padding: 32px;
  padding-left: 0px;
  width: 100%;
  display: flex;
  justify-content: flex-start;

  @media screen and (max-width: 768px) {
    justify-content: left;
  }
`;

export const DetailsBoxStepThree = styled.div`
  margin-bottom: 0px;
  width: 100%;
  border-top: 1px solid #d9d9dd;
  border-bottom: 0px solid;
  display: flex;
  padding: 0px;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: 768px) {
    padding: 8px 0;
  }
`;

export const lableStepThree = styled(Label)`
  padding: 16px;
`;

export const ErrorMessage = styled(Label)`
  padding: 16px;
`;

export const IconBox = styled(Box)`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  /* The format/size label wraps to two lines, so a fixed 40px height made
     it spill past the row and sit out of line with the upload box. */
  min-height: 40px;
  gap: 16px;
  width: 100%;
  @media screen and (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    justify-content: right;
    align-items: center;
  }
`;

export const DividerIcon = styled('div')`
  width: 100%;
  height: 1px;
  background-color: #E5E5E8;
  margin: 10px 0;
  @media screen and (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: auto;
  }
`;

export const ButtonHelpApp = styled('div')`
  width: 281px;
  height: 48px;
  border-radius: 8px;
  margin-bottom: 16px;
  background-color: #EBF5FF;
  color: #1E88E5;
  font-size: 11px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  line-height: 16px;
  text-transform: none;
  text-align: right;
  box-shadow: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 12px;
  padding-bottom: 10px;
  &:hover {
    background-color: #ededed;
  }
`;

export const ImageBox = styled(Box)`
  display: flex;
  padding-top: 16px;
  margin-bottom: auto;
  flex-direction: row;
  direction: rtl;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  /* min-height, not a fixed 230px: the gallery inside is taller than that, so
     a hard height let it spill past the card that is meant to contain it. */
  min-height: 230px;
  flex-wrap: nowrap;
  gap: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    margin: 4px;
    margin-top: 30px;
    margin-bottom: 30px;
    align-items: center;
  }
`;

export const ScreenshotContainer = styled(Box)`
  display: flex;
  padding-top: 16px;
  margin-bottom: auto;
  flex-direction: row;
  direction: rtl;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 230px;
  flex-wrap: nowrap;
  gap: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    margin: 4px;
    margin-top: 30px;
    margin-bottom: 30px;
    align-items: center;
  }
`;

export const BoxStepFour = styled('SubmitBox')`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: auto;
  @media (max-width: 768px) {
    max-width: 350px;
  }
`;

export const ErrorText = styled.span`
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
`;